import React, { useState } from "react";
import { motion } from "framer-motion";

const tracks = [
  {
    range: "Primary · Grades 1—4",
    color: "#FCA311",
    subjects: ["Writing & Reading", "Mental Math", "EVS ", "Interpretation Skills"],
  },
  {
    range: "Middle · Grades 5—8",
    color: "#4ECDC4",
    subjects: ["Mathematics", "Science", "English Lit.", "Social Studies", "Hindi / Malayalam"],
  },
  {
    range: "Secondary · Grades 9—10",
    color: "#FF6B6B",
    subjects: ["Conceptual Mathematics", "Physics", "Chemistry", "Biology", "English/Hindi/Malayalam","Social Science", "Board Mock Series"],
  },
  {
    range: "Senior · Grades 11—12",
    color: "#0B132B",
    subjects: [ "Commerce + Maths", "Biology", "Physics","Chemistry","Computer Science", "Board Crash Course", "College Counselling"],
  },
];

export default function Subjects() {
  const [active, setActive] = useState(0);
  return (
    <section
      id="subjects"
      data-testid="subjects-section"
      className="px-4 sm:px-6 md:px-12 lg:px-16 py-20 sm:py-24 md:py-32"
      style={{ background: "var(--bg-primary)" }}
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16 max-w-3xl">
          <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-[#FF6B6B] mb-4">
            ✦ Subjects & tracks
          </div>
          <h2 className="font-display font-black text-3xl sm:text-5xl md:text-6xl text-[#0B132B] tracking-tighter leading-[0.95]">
            Twelve grades. <br />One curriculum that grows up with you.
          </h2>
        </div>

        <div className="hidden lg:grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 flex flex-col gap-3">
            {tracks.map((t, i) => (
              <button
                key={i}
                data-testid={`subject-tab-${i}`}
                onClick={() => setActive(i)}
                className={`text-left rounded-2xl p-4 sm:p-5 border transition-all duration-300 ${
                  active === i
                    ? "border-transparent shadow-[0_12px_30px_rgba(11,19,43,0.10)]"
                    : "border-[#0B132B]/10 hover:border-[#0B132B]/30"
                }`}
                style={{
                  background: active === i ? t.color : "transparent",
                  color: active === i ? "#fff" : "#0B132B",
                }}
              >
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold opacity-80">
                  Track {i + 1}
                </div>
                <div className="font-display text-xl md:text-2xl font-bold tracking-tight mt-1">
                  {t.range}
                </div>
              </button>
            ))}
          </div>

          <motion.div
            key={active}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-8 rounded-3xl p-6 sm:p-8 md:p-12 border border-[#0B132B]/8"
            style={{ background: "#FFFFFF" }}
            data-testid="subjects-panel"
          >
            <div
              className="text-[11px] tracking-[0.25em] uppercase font-bold mb-3"
              style={{ color: tracks[active].color }}
            >
              {tracks[active].range}
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-black text-[#0B132B] tracking-tighter mb-8">
              What students master here
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tracks[active].subjects.map((s, i) => (
                <div
                  key={s}
                  className="flex items-center gap-3 p-4 rounded-2xl"
                  style={{ background: "var(--bg-secondary)" }}
                >
                  <span
                    className="w-8 h-8 rounded-full grid place-items-center text-xs font-bold text-white"
                    style={{ background: tracks[active].color }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-medium text-[#0B132B]">{s}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <div className="lg:hidden space-y-4">
          {tracks.map((t, i) => (
            <div
              key={t.range}
              className={`rounded-2xl border ${
                active === i ? "border-transparent shadow-[0_12px_30px_rgba(11,19,43,0.10)]" : "border-[#0B132B]/10"
              }`}
              style={{ background: active === i ? t.color : "#FFFFFF" }}
            >
              <button
                type="button"
                data-testid={`subject-dropdown-${i}`}
                onClick={() => setActive(i)}
                className="w-full text-left p-4 sm:p-5"
                style={{ color: active === i ? "#fff" : "#0B132B" }}
              >
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold opacity-80">
                  Track {i + 1}
                </div>
                <div className="font-display text-lg sm:text-xl font-bold tracking-tight mt-1">
                  {t.range}
                </div>
              </button>
              {active === i && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35 }}
                  className="px-4 pb-5 sm:px-5"
                >
                  <div className="text-xs uppercase tracking-[0.25em] font-bold mb-3" style={{ color: "#fff" }}>
                    Subjects
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {t.subjects.map((s, idx) => (
                      <div
                        key={s}
                        className="flex items-center gap-3 p-3 rounded-2xl"
                        style={{ background: "rgba(255,255,255,0.18)", color: "#fff" }}
                      >
                        <span className="w-7 h-7 rounded-full grid place-items-center text-xs font-bold bg-white/90 text-[#0B132B]">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className="font-medium">{s}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
